import React, { useEffect, useMemo } from "react";
import LinksAdmin from "./LinksAdmin";
import Comments from "./Comments";
import blog from "./blog.json";
import { useAuth } from "../auth";

type Props = {
  filterText: string;
};

const Blog = (props: Props) => {
  // Mark legacy (bundled) posts with a source so we can visually separate sections
  const legacyAsc = useMemo(() => blog.map((p: any) => ({ ...p, _source: "legacy" as const })), []);

  const [filteredBlog, setFilteredBlog] = React.useState(legacyAsc as any[]);
  const [fullBlog, setFullBlog] = React.useState(legacyAsc as any[]);
  const [filteredPosts, setFilteredPosts] = React.useState(legacyAsc.length);
  const [page, setPage] = React.useState(0);
  const [subjectFilter, setSubjectFilter] = React.useState<string | null>(null);
  const [editMode, setEditMode] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState<any | null>(null);
  const [adminPw, setAdminPw] = React.useState<string>("");
  const [linksAdminOpen, setLinksAdminOpen] = React.useState<boolean>(false);
  const [kvLoading, setKvLoading] = React.useState<boolean>(false);
  // When preparing to promote a legacy post, track which card is in edit
  const [promoteEditingAnchorId, setPromoteEditingAnchorId] = React.useState<string | null>(null);
  // AI image generation state (mirrors blog entry)
  const [sdApiKey, setSdApiKey] = React.useState<string>("");
  const [genPrompt, setGenPrompt] = React.useState<string>("");
  const [genLoading, setGenLoading] = React.useState<boolean>(false);
  const [genError, setGenError] = React.useState<string | null>(null);
  const [genPreviewUrl, setGenPreviewUrl] = React.useState<string | null>(null);
  // Promote legacy->KV busy handle
  const [promoteBusyId, setPromoteBusyId] = React.useState<string | null>(null);
  const pageSize = 5;
  // Use normal pagination across all filtered posts
  const { isLoggedIn, token } = useAuth();

  // function to remove any html form a given string
  function removeHtmlTags(str: string) {
    return str.replace(/<[^>]*>?/gm, "");
  }

  // detect if the body contains embeddable elements we want to render
  function hasEmbeddable(html: string | null | undefined): boolean {
    if (!html) return false;
    return /<(object|embed|iframe|video|audio)\b/i.test(html);
  }

  // very small sanitizer to strip scripts and unsafe event handlers while allowing embeds
  function sanitizeHtmlAllowEmbeds(html: string): string {
    let out = html;
    // remove script tags
    out = out.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
    // remove on* event handler attributes (quoted or unquoted)
    out = out.replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
    // neutralize javascript: URIs in href/src
    out = out.replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[^'\"]*\2/gi, ' $1="#"');
    return out;
  }

  // Build a stable anchor id for each post for permalink linking
  function slugifyForId(str: string): string {
    const plain = removeHtmlTags(String(str || ""));
    return plain
      .toLowerCase()
      .replace(/&[a-z0-9#]+;/gi, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
  }

  // Date helpers for edit UI
  function toYMD(s: string): string {
    const m = String(s || "").match(/(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  }
  function toHM(s: string): string {
    const m = String(s || "").match(/\b(\d{2}:\d{2})\b/);
    return m ? m[1] : "";
  }
  function mergeDateTime(ymd: string, hm: string): string {
    const d = String(ymd || "").trim();
    const t = String(hm || "").trim();
    if (d && t) return `${d} ${t}`;
    if (d) return d;
    return t; // fallback; likely ignored by parser without date
  }

  function getPostAnchorId(post: any, fallbackIndex?: number): string {
    const direct = post?.ID ?? post?.id ?? post?._id ?? post?.slug;
    if (direct != null && String(direct).trim() !== "") {
      return `post-${String(direct)}`;
    }
    const titlePart = slugifyForId(post?.Title);
    const datePart = (post?.date ? String(post.date) : "").replace(/[^0-9]+/g, "").slice(0, 8);
    const composed = [titlePart, datePart].filter(Boolean).join("-");
    if (composed) return `post-${composed}`;
    return `post-${fallbackIndex ?? 0}`;
  }

  // Extract first image src from an HTML snippet and resolve allowed sources
  function extractDisplayImageSrc(htmlOrArray: string | string[] | null | undefined): string | null {
    if (!htmlOrArray) return null;
    // Support array form (some entries) by taking first item
    if (Array.isArray(htmlOrArray)) {
      const first = htmlOrArray.find((s) => typeof s === "string" && s.trim().length > 0);
      if (!first) return null;
      const raw = first.trim();
      if (/^https?:\/\//i.test(raw)) return raw; // external
      if (/^\/?images\//i.test(raw)) return raw.startsWith("/") ? raw : `/${raw}`; // public/images/*
      if (raw.startsWith("data:")) return raw; // data URL from generator
      // fall through to HTML parsing below
    }

    const val = Array.isArray(htmlOrArray) ? String(htmlOrArray[0] ?? "") : String(htmlOrArray);
    const s = val.trim();
    if (!s) return null;
    // Newer posts may store a direct URL/path string
    if (/^https?:\/\//i.test(s)) return s;
    if (/^data:/i.test(s)) return s;
    if (/^\/?images\//i.test(s)) return s.startsWith("/") ? s : `/${s}`;

    // Otherwise treat as HTML and extract first <img src="...">
    const imgTagMatch = s.match(/<img\b[^>]*src=["']([^"'>\s]+)["'][^>]*>/i);
    if (!imgTagMatch) return null;
    const raw = imgTagMatch[1];
    if (/^https?:\/\//i.test(raw)) return raw; // external
    if (/^\/?images\//i.test(raw)) return raw.startsWith("/") ? raw : `/${raw}`; // public/images/*
    return null;
  }

  // derive all unique subjects from the data
  const allSubjects = useMemo(() => {
    const set = new Set<string>();
    fullBlog.forEach((p: any) => {
      const subj = (p?.subject || "").toString().trim();
      if (subj) set.add(subj);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [fullBlog]);

  // recompute filtered list whenever search text or subject filter changes
  useEffect(() => {
    const term = (props.filterText ?? "").toLowerCase();
    const filtered = fullBlog.filter((eachOne: any) => {
      const matchText = term === "" || removeHtmlTags(eachOne.Title).toLowerCase().includes(term) || removeHtmlTags(eachOne.Body).toLowerCase().includes(term);
      const matchSubject = !subjectFilter || (eachOne.subject || "") === subjectFilter;
      return matchText && matchSubject;
    });
    // Use entire filtered set; pagination below will handle slicing
    setFilteredBlog(filtered);
    setFilteredPosts(filtered.length);
    setPage(0); // reset pagination when filters change
  }, [props.filterText, subjectFilter, fullBlog]);

  // Load latest posts from KV on mount and merge with local posts
  useEffect(() => {
    let cancelled = false;
    const fetchLatest = async () => {
      setKvLoading(true);
      try {
        const url = isLoggedIn ? "/api/get-posts?limit=100&include=all" : "/api/get-posts?limit=5";
        const res = await fetch(url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } as any });
        if (!res.ok) return;
        const data = await res.json();
        const posts: any[] = Array.isArray(data) ? data : Array.isArray(data?.posts) ? data.posts : [];
        if (!posts.length) return;
        // API returns newest-first; convert to oldest-first so that our
        // display reversal later yields newest-first across both sources.
        const normalizedDesc = posts
          .map((p) => ({
            ...p,
            Title: p?.Title ?? p?.title ?? "",
            Body: p?.Body ?? p?.content ?? "",
            _source: "kv" as const,
          }))
          // keep only posts with non-empty body
          .filter((p) => (p?.Body || "").toString().trim().length > 0);
        if (!cancelled && normalizedDesc.length) {
          // Build ascending order (oldest -> newest) so our render-time
          // reverse shows newest first, with KV posts before legacy posts.
          const normalizedAsc = [...normalizedDesc].reverse();
          const mergedAsc = [...legacyAsc, ...normalizedAsc];
          setFullBlog(mergedAsc);
          setFilteredBlog(mergedAsc);
          setFilteredPosts(mergedAsc.length);
          setPage(0);
        }
      } catch {
        // ignore network errors; fallback to bundled blog.json
      } finally {
        if (!cancelled) setKvLoading(false);
      }
    };
    fetchLatest();
    return () => {
      cancelled = true;
    };
  }, [legacyAsc, isLoggedIn, token]);

  // Normalize links input to a clean string array
  function normalizeLinks(linksMaybe: any): string[] {
    if (!linksMaybe) return [];
    if (Array.isArray(linksMaybe)) {
      return linksMaybe.map((s) => (typeof s === "string" ? s.trim() : "")).filter((s) => s.length > 0);
    }
    // Support comma separated string fallback
    if (typeof linksMaybe === "string") {
      return linksMaybe
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    return [];
  }

  // Extract a safe YouTube ID from a raw value (ID or URL)
  function safeYouTubeId(raw: any): string | null {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    // If a full URL was pasted, try to extract the id
    try {
      const u = new URL(s);
      // youtu.be/<id>
      if (/^(www\.)?youtu\.be$/i.test(u.hostname)) {
        const id = u.pathname.replace(/^\//, "");
        const clean = id.replace(/[^a-zA-Z0-9_-]/g, "");
        return clean || null;
      }
      // youtube.com/watch?v=<id>
      if (/^(www\.)?youtube\.com$/i.test(u.hostname)) {
        const id = u.searchParams.get("v") || "";
        const clean = id.replace(/[^a-zA-Z0-9_-]/g, "");
        if (clean) return clean;
        // youtube.com/embed/<id>
        const parts = u.pathname.split("/").filter(Boolean);
        if (parts[0] === "embed" && parts[1]) {
          const emb = parts[1].replace(/[^a-zA-Z0-9_-]/g, "");
          return emb || null;
        }
      }
    } catch {}
    // Otherwise treat as a bare id
    const cleaned = s.replace(/[^a-zA-Z0-9_-]/g, "");
    return cleaned || null;
  }

  // Load saved admin password for edit actions
  useEffect(() => {
    try {
      const pw = localStorage.getItem("blog_entry_password");
      if (pw) setAdminPw(pw);
    } catch {}
    try {
      const saved = localStorage.getItem("stability_api_key");
      if (saved) setSdApiKey(saved);
    } catch {}
  }, []);

  // Keyboard chord: press Q + W + E at the same time to toggle edit mode
  useEffect(() => {
    const pressed = new Set<string>();
    let clearTimer: number | null = null;
    const reset = () => {
      pressed.clear();
      if (clearTimer) {
        window.clearTimeout(clearTimer);
        clearTimer = null;
      }
    };
    const onDown = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === "q" || k === "w" || k === "e") {
        pressed.add(k);
        // auto-clear after short window to enforce simultaneity
        if (clearTimer) window.clearTimeout(clearTimer);
        clearTimer = window.setTimeout(() => reset(), 600);
        if (pressed.has("q") && pressed.has("w") && pressed.has("e")) {
          if (!isLoggedIn) {
            alert("Please sign in with Google to use admin features.");
            reset();
            return;
          }
          setEditMode((v) => !v);
          setEditingId(null);
          setEditDraft(null);
          reset();
        }
      }
    };
    const onUp = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (k === "q" || k === "w" || k === "e") {
        pressed.delete(k);
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      if (clearTimer) window.clearTimeout(clearTimer);
    };
  }, [isLoggedIn]);

  function beginEdit(post: any) {
    const id = String(post?.id ?? post?.ID ?? "");
    if (!id) return;
    setEditingId(id);
    const title = String(post?.Title ?? post?.title ?? "");
    const content = String(post?.Body ?? post?.content ?? "");
    const date = String(post?.date ?? "");
    const subject = String(post?.subject ?? "");
    const status = String((post?.status || "published")).toLowerCase() === "draft" ? "draft" : "published";
    const hidden = !!post?.hidden;
    const imagesVal = post?.images;
    const linksVal = post?.links;
    const youtubeId = String(post?.youtubeId ?? "");
    const imagesStr = Array.isArray(imagesVal) ? imagesVal.filter((s: any) => typeof s === "string" && s.trim().length > 0).join(", ") : imagesVal ? String(imagesVal) : "";
    const linksStr = Array.isArray(linksVal) ? linksVal.filter((s: any) => typeof s === "string" && s.trim().length > 0).join(", ") : linksVal ? String(linksVal) : "";
    setEditDraft({ id, title, content, date, subject, status, hidden, imagesStr, linksStr, youtubeId });
  }

  // Begin an edit session for a legacy post prior to adding to KV
  function beginPromoteEdit(post: any, anchorId: string) {
    setPromoteEditingAnchorId(anchorId);
    setEditingId(null);
    const title = String(post?.Title ?? post?.title ?? "");
    const content = String(post?.Body ?? post?.content ?? "");
    const date = String(post?.date ?? "");
    const subject = String(post?.subject ?? "");
    const status = "published" as const;
    const hidden = false;
    const imagesVal = post?.images;
    const linksVal = normalizeLinks(post?.links);
    const youtubeId = String((post as any)?.youtubeId ?? "");
    const firstImg = extractDisplayImageSrc(imagesVal);
    const imagesStr = firstImg ? firstImg : (Array.isArray(imagesVal) ? imagesVal.find((s: any) => typeof s === "string" && s.trim().length > 0) : typeof imagesVal === "string" ? imagesVal : "") || "";
    const linksStr = (linksVal || []).join(", ");
    setEditDraft({ title, content, date, subject, status, hidden, imagesStr, linksStr, youtubeId });
    // reset AI generator state for this session
    setGenPrompt("");
    setGenError(null);
    setGenPreviewUrl(null);
  }

  // Generate AI image (adds a data URL as first image in edit draft)
  async function generateImageForEdit() {
    setGenError(null);
    if (!editDraft) {
      setGenError("No post is being edited.");
      return;
    }
    if (!genPrompt.trim()) {
      setGenError("Please enter a prompt.");
      return;
    }
    if (!sdApiKey.trim()) {
      setGenError("Please enter your Stability API key.");
      return;
    }
    setGenLoading(true);
    try {
      const form = new FormData();
      form.append("prompt", genPrompt.trim());
      form.append("output_format", "jpeg");
      form.append("model", "sd3.5-large-turbo");
      form.append("aspect_ratio", "21:9");

      const res = await fetch("https://api.stability.ai/v2beta/stable-image/generate/sd3", {
        method: "POST",
        headers: {
          authorization: `Bearer ${sdApiKey.trim()}`,
          accept: "image/*",
        },
        body: form,
      });
      if (!res.ok) {
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          const j = await res.json();
          throw new Error(j?.error || j?.message || `Request failed: ${res.status}`);
        }
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }
      const blob = await res.blob();
      const reader = new FileReader();
      const dataUrl: string = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(String(reader.result || ""));
        reader.onerror = reject as any;
        reader.readAsDataURL(blob);
      });
      setGenPreviewUrl(dataUrl);
      // Prepend the generated image data URL into images string
      setEditDraft((prev: any) => {
        if (!prev) return prev;
        const existing = String(prev.imagesStr || "").trim();
        // For saving logic, if the field starts with data:, the whole string is treated as a single data URL
        // so prefer to set it directly to the generated image.
        return { ...prev, imagesStr: dataUrl };
      });
    } catch (err: any) {
      setGenError(err?.message || "Failed to generate image");
    } finally {
      setGenLoading(false);
    }
  }

  // Backfill a legacy (bundled) post into KV with original date timestamp
  async function promoteLegacyToKv(post: any) {
    const pw = (adminPw || "").trim();
    if (!pw) {
      alert("Enter admin password to add posts.");
      return;
    }
    // Use anchor id as temporary key for busy state
    const busyId = getPostAnchorId(post);
    setPromoteBusyId(busyId);
    try {
      const title = String(post?.Title ?? post?.title ?? "");
      const content = String(post?.Body ?? post?.content ?? "");
      const dateStr = String(post?.date ?? "");
      const subject = String(post?.subject ?? "").trim() || undefined;
      const links = normalizeLinks(post?.links);
      const imgStr = (() => {
        const first = extractDisplayImageSrc(post?.images);
        if (first) return first;
        const val = post?.images;
        if (typeof val === "string" && val.trim()) return val.trim();
        if (Array.isArray(val)) {
          const s = val.find((x) => typeof x === "string" && x.trim().length > 0);
          if (s) return s.trim();
        }
        return undefined as undefined | string;
      })();
      // Compute key timestamp from original date (YYYY-MM-DD or ISO). Fallback to now if invalid
      let keyTs = Date.now();
      const parsed = Date.parse(dateStr);
      if (!Number.isNaN(parsed)) keyTs = parsed;

      const payload: any = {
        password: pw,
        title,
        Title: title,
        content,
        Body: content,
        date: dateStr,
        subject,
        images: imgStr,
        links,
        youtubeId: String((post as any).youtubeId || "").trim() || undefined,
        keyTs,
      };

      const res = await fetch("/api/blog", {
        method: "POST",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Failed with ${res.status}`);
      }
      // Reload KV posts so the newly added post appears under Latest Posts in correct order
      await reloadKvPosts();
      alert("Added to Latest posts.");
    } catch (err: any) {
      alert(err?.message || "Failed to add to Latest");
    } finally {
      setPromoteBusyId(null);
    }
  }

  function cancelPromoteEdit() {
    setPromoteEditingAnchorId(null);
    setEditDraft(null);
    setGenPrompt("");
    setGenError(null);
    setGenPreviewUrl(null);
  }

  // Save the edited legacy post into KV with backdated key
  async function savePromoteEdit(anchorId: string) {
    if (!editDraft) return;
    const pw = (adminPw || "").trim();
    if (!isLoggedIn && !pw) {
      alert("Sign in (or enter admin password) to add posts.");
      return;
    }
    // Prepare images & links like saveEdit
    const firstNonEmpty = (arr: string[]) => arr.find((s) => s.trim().length > 0) || "";
    const imagesInput = String(editDraft.imagesStr || "").trim();
    const imagesFirst = imagesInput.startsWith("data:") ? imagesInput : firstNonEmpty(imagesInput.split(",").map((s) => s.trim()));
    const linksArr = String(editDraft.linksStr || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    // Compute key timestamp from edited date
    let keyTs = Date.now();
    const parsed = Date.parse(String(editDraft.date || ""));
    if (!Number.isNaN(parsed)) keyTs = parsed;

    const payload: any = {
      password: pw,
      title: String(editDraft.title || ""),
      Title: String(editDraft.title || ""),
      content: String(editDraft.content || ""),
      Body: String(editDraft.content || ""),
      date: String(editDraft.date || ""),
      subject: String(editDraft.subject || "").trim() || undefined,
      images: imagesFirst || undefined,
      links: linksArr,
      youtubeId: String(editDraft.youtubeId || "").trim() || undefined,
      status: String(editDraft.status || "published").toLowerCase() === "draft" ? "draft" : "published",
      hidden: !!editDraft.hidden,
      keyTs,
    };

    setPromoteBusyId(anchorId);
    try {
      const res = await fetch("/api/blog", {
        method: "POST",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Failed with ${res.status}`);
      }
      await reloadKvPosts();
      alert("Added to Latest posts.");
      cancelPromoteEdit();
    } catch (err: any) {
      alert(err?.message || "Failed to add to Latest");
    } finally {
      setPromoteBusyId(null);
    }
  }

  async function reloadKvPosts() {
    setKvLoading(true);
    try {
      const url = isLoggedIn ? "/api/get-posts?limit=100&include=all" : "/api/get-posts?limit=5";
      const res = await fetch(url, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } as any });
      if (!res.ok) return;
      const data = await res.json();
      const posts: any[] = Array.isArray(data) ? data : Array.isArray(data?.posts) ? data.posts : [];
      if (!posts.length) return;
      const normalizedDesc = posts
        .map((p) => ({
          ...p,
          Title: p?.Title ?? p?.title ?? "",
          Body: p?.Body ?? p?.content ?? "",
          _source: "kv" as const,
        }))
        .filter((p) => (p?.Body || "").toString().trim().length > 0);
      if (normalizedDesc.length) {
        const normalizedAsc = [...normalizedDesc].reverse();
        const mergedAsc = [...legacyAsc, ...normalizedAsc];
        setFullBlog(mergedAsc);
        setFilteredBlog(mergedAsc);
        setFilteredPosts(mergedAsc.length);
        setPage(0);
      }
    } catch {
    } finally {
      setKvLoading(false);
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(null);
  }

  async function saveEdit() {
    if (!editDraft || !editingId) return;
    const pw = (adminPw || "").trim();
    if (!isLoggedIn && !pw) {
      alert("Sign in (or enter admin password) to save changes.");
      return;
    }
    const firstNonEmpty = (arr: string[]) => arr.find((s) => s.trim().length > 0) || "";
    // Preserve full data URLs (which contain a comma after the header)
    const imagesInput = String(editDraft.imagesStr || "").trim();
    const imagesFirst = imagesInput.startsWith("data:") ? imagesInput : firstNonEmpty(imagesInput.split(",").map((s) => s.trim()));
    const linksArr = String(editDraft.linksStr || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const payload: any = {
      password: pw,
      title: String(editDraft.title || ""),
      Title: String(editDraft.title || ""),
      content: String(editDraft.content || ""),
      Body: String(editDraft.content || ""),
      date: String(editDraft.date || ""),
      subject: String(editDraft.subject || "").trim() || undefined,
      images: imagesFirst || undefined,
      links: linksArr,
      youtubeId: String(editDraft.youtubeId || "").trim() || undefined,
      status: String(editDraft.status || "published").toLowerCase() === "draft" ? "draft" : "published",
      hidden: !!editDraft.hidden,
    };

    try {
      const res = await fetch(`/api/blog/${encodeURIComponent(editingId)}`, {
        method: "PATCH",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Failed with ${res.status}`);
      }
      // Update local state for the edited post
      setFullBlog((prev) =>
        prev.map((p: any) => {
          const pid = String(p?.id ?? p?.ID ?? "");
          if (pid !== editingId) return p;
          return {
            ...p,
            title: payload.title,
            Title: payload.Title,
            content: payload.content,
            Body: payload.Body,
            date: payload.date,
            subject: payload.subject,
            status: payload.status,
            hidden: payload.hidden,
            images: payload.images ?? p.images,
            links: payload.links,
            youtubeId: payload.youtubeId ?? p.youtubeId,
          };
        })
      );
      // Also update filtered list immediately for UX
      setFilteredBlog((prev) =>
        prev.map((p: any) => {
          const pid = String(p?.id ?? p?.ID ?? "");
          if (pid !== editingId) return p;
          return {
            ...p,
            title: payload.title,
            Title: payload.Title,
            content: payload.content,
            Body: payload.Body,
            date: payload.date,
            subject: payload.subject,
            status: payload.status,
            hidden: payload.hidden,
            images: payload.images ?? p.images,
            links: payload.links,
            youtubeId: payload.youtubeId ?? p.youtubeId,
          };
        })
      );
      cancelEdit();
    } catch (err: any) {
      alert(err?.message || "Failed to save changes");
    }
  }

  async function deletePost(pid: string) {
    const pw = (adminPw || "").trim();
    if (!isLoggedIn && !pw) {
      alert("Sign in (or enter admin password) to delete posts.");
      return;
    }
    const ok = window.confirm("Delete this post? This cannot be undone.");
    if (!ok) return;
    try {
      const res = await fetch(`/api/blog/${encodeURIComponent(pid)}`, {
        method: "DELETE",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ password: pw }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Failed with ${res.status}`);
      }
      // remove from lists
      setFullBlog((prev) => prev.filter((p: any) => String(p?.id ?? p?.ID ?? "") !== pid));
      setFilteredBlog((prev) => {
        const next = prev.filter((p: any) => String(p?.id ?? p?.ID ?? "") !== pid);
        setFilteredPosts(next.length);
        return next;
      });
      if (editingId === pid) cancelEdit();
    } catch (err: any) {
      alert(err?.message || "Failed to delete post");
    }
  }

  // Scroll to top whenever the page changes (e.g., Next/Previous)
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [page]);

  // Hide failed embeds inside rendered HTML (iframes, embeds, objects, media)
  useEffect(() => {
    const containers = Array.from(document.querySelectorAll<HTMLDivElement>(".post-html"));
    const cleanups: Array<() => void> = [];
    const timeouts: number[] = [];

    const hide = (el: Element) => {
      (el as HTMLElement).style.display = "none";
    };

    containers.forEach((container) => {
      const selector = "img, iframe, embed, object, video, audio";
      const elements = Array.from(container.querySelectorAll(selector));
      elements.forEach((el) => {
        // Track whether the element reports as loaded
        let loaded = false;

        const onLoad = () => {
          loaded = true;
        };
        const onLoadedData = () => {
          loaded = true;
        };
        const onError = () => {
          hide(el);
        };

        // Attach listeners where supported
        el.addEventListener("load", onLoad as any);
        el.addEventListener("loadeddata", onLoadedData as any);
        el.addEventListener("error", onError as any);

        cleanups.push(() => {
          el.removeEventListener("load", onLoad as any);
          el.removeEventListener("loadeddata", onLoadedData as any);
          el.removeEventListener("error", onError as any);
        });

        // Fallback: if no load event within a reasonable time, hide it
        const t = window.setTimeout(() => {
          if (!loaded) hide(el);
        }, 7000);
        timeouts.push(t);
      });
    });

    return () => {
      cleanups.forEach((fn) => fn());
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, [filteredBlog, page, subjectFilter]);

  return (
    <section className="blog-brutalist">
      {allSubjects.length > 0 ? (
        <div className="subject-filter" role="toolbar" aria-label="Filter by subject">
          <span className="count-badge count-badge--big">{filteredPosts} posts</span>
          {/* Desktop/Wide: buttons */}
          <div className="subject-buttons">
            <button className={`subject-btn${subjectFilter ? "" : " active"}`} onClick={() => setSubjectFilter(null)}>
              All
            </button>
            {allSubjects.map((subj) => (
              <button key={subj} className={`subject-btn${subjectFilter === subj ? " active" : ""}`} onClick={() => setSubjectFilter(subj)}>
                {subj}
              </button>
            ))}
          </div>
          {/* Mobile/Narrow: select */}
          <div className="subject-select-wrap">
            <select
              className="subject-select"
              value={subjectFilter ?? "__ALL__"}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setSubjectFilter(v === "__ALL__" ? null : v);
              }}
              aria-label="Select subject"
            >
              <option value="__ALL__">All subjects</option>
              {allSubjects.map((subj) => (
                <option key={subj} value={subj}>
                  {subj}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <header className="blog-header">
          <span className="count-badge">{filteredPosts} posts</span>
        </header>
      )}

      {(() => {
        const filteredNonEmpty = filteredBlog.filter((eachOne) => removeHtmlTags(eachOne.Body) !== "");
        const ordered = [...filteredNonEmpty].reverse(); // newest first
        const totalPosts = ordered.length;
        const totalPages = Math.ceil(totalPosts / pageSize);
        const currentPage = totalPages === 0 ? 0 : Math.min(page, totalPages - 1);
        const start = currentPage * pageSize;
        const end = start + pageSize;
        const pagePosts = ordered.slice(start, end);

        // Determine first index of each section within this page to insert headings
        const firstKvIndex = pagePosts.findIndex((p: any) => p?._source === "kv");
        const firstLegacyIndex = pagePosts.findIndex((p: any) => p?._source !== "kv");

        return (
          <>
            {/* Show loading placeholder in the posts area while KV posts are being fetched */}
            {kvLoading && !fullBlog.some((p: any) => p?._source === "kv") && (
              <div className="loading-card" role="status" aria-live="polite">
                <div className="loading-inner">
                  <div className="loading-dots" aria-hidden>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="loading-text">Loading latest posts...</div>
                </div>
              </div>
            )}
            {/* Edit mode toolbar */}
            {editMode && (
              <div className="edit-toolbar" role="region" aria-label="Admin editing controls">
                <span className="edit-mode-pill">Edit Mode</span>
                <div className="pw-wrap">
                  <input
                    type="password"
                    value={adminPw}
                    placeholder="Admin password"
                    onChange={(e) => {
                      setAdminPw(e.target.value);
                      try {
                        localStorage.setItem("blog_entry_password", e.target.value);
                      } catch {}
                    }}
                  />
                  <button
                    className="btn"
                    onClick={() => {
                      setAdminPw("");
                      try {
                        localStorage.removeItem("blog_entry_password");
                      } catch {}
                    }}
                  >
                    Clear
                  </button>
                </div>
                <button className="btn" onClick={() => setLinksAdminOpen(true)}>
                  Manage Websites
                </button>
              </div>
            )}

            <div className="post-list">
              {pagePosts.map((post, index) => {
                const anchorId = getPostAnchorId(post, start + index);
                const pid = String(post?.id ?? post?.ID ?? "");
                const canEdit = editMode && !!pid && post?._source === "kv";
                return (
                  <React.Fragment key={index}>
                    {index === firstKvIndex && (
                      <h3 className="section-heading section-heading--latest" aria-label="Latest posts">
                        Latest Posts
                      </h3>
                    )}
                    {index === firstLegacyIndex && (
                      <h3 className="section-heading section-heading--older" aria-label="Older posts">
                        Older Posts
                      </h3>
                    )}

                    <article className="post-card" id={anchorId}>
                      <div className="post-col-left">
                        {(() => {
                          const isKv = post?._source === "kv" && !!pid;
                          const linkHref = isKv ? `#/blog/${encodeURIComponent(pid)}` : `#${anchorId}`;
                          const linkTitle = isKv ? "Open single post page" : "Permalink";
                          return (
                            <a className="post-anchor" href={linkHref} aria-label="Open post" title={linkTitle}>
                              <img src="/dagger.png" alt="" className="post-anchor-icon" />
                            </a>
                          );
                        })()}
                      </div>
                      <div className="post-col-main">
                        <div className="post-title-row">
                          {(() => {
                            const showGenThumb = !!genPreviewUrl && (promoteEditingAnchorId === anchorId || editingId === pid);
                            if (!showGenThumb) return null;
                            return <img className="gen-thumb" src={genPreviewUrl as string} alt="Generated thumbnail" />;
                          })()}
                          <h2 className="post-title">{removeHtmlTags(post.Title)}</h2>
                        </div>
                        {editMode && post?._source !== "kv" && (
                          <div className="edit-actions">
                            {promoteEditingAnchorId === anchorId ? (
                              <>
                                <button className="btn btn-primary" onClick={() => savePromoteEdit(anchorId)} disabled={promoteBusyId === anchorId}>
                                  {promoteBusyId === anchorId ? "Adding..." : "Save & Add"}
                                </button>
                                <button className="btn" onClick={cancelPromoteEdit}>
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="btn" onClick={() => beginPromoteEdit(post, anchorId)}>
                                  Edit & Add
                                </button>
                                <button className="btn btn-primary" onClick={() => promoteLegacyToKv(post)} disabled={promoteBusyId === anchorId}>
                                  {promoteBusyId === anchorId ? "Adding..." : "Add to Latest"}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {canEdit && (
                          <div className="edit-actions">
                            {editingId === pid ? (
                              <>
                                <button className="btn btn-primary" onClick={saveEdit}>
                                  Save
                                </button>
                                <button className="btn" onClick={cancelEdit}>
                                  Cancel
                                </button>
                                <button className="btn btn-danger" onClick={() => deletePost(pid)}>
                                  Delete
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="btn" onClick={() => beginEdit(post)}>
                                  Edit
                                </button>
                                <button className="btn btn-danger" onClick={() => deletePost(pid)}>
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        {post.date && <div className="post-meta">{post.date}</div>}
                        {/* Render YouTube embed if provided */}
                        {(() => {
                          const id = safeYouTubeId((post as any).youtubeId);
                          if (!id) return null;
                          const src = `https://www.youtube.com/embed/${id}`;
                          return (
                            <div className="yt-embed" aria-label="YouTube video">
                              <iframe src={src} title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                            </div>
                          );
                        })()}
                        {/* Render image; if there is exactly one link, wrap image to open that link */}
                        {(() => {
                          const imgSrc = extractDisplayImageSrc((post as any).images);
                          if (!imgSrc) return null;
                          const postLinks = normalizeLinks((post as any).links);
                          const imgEl = (
                            <img
                              className="post-image"
                              src={imgSrc as string}
                              alt="Post image"
                              loading="lazy"
                              onError={(e) => {
                                // If the image fails to load (404, etc.), hide it
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          );
                          if (postLinks.length === 1) {
                            return (
                              <a href={postLinks[0]} target="_blank" rel="noopener noreferrer">
                                {imgEl}
                              </a>
                            );
                          }
                          return imgEl;
                        })()}
                        {(editingId === pid && editDraft) || (promoteEditingAnchorId === anchorId && editDraft) ? (
                          <div className="edit-form">
                            <label className="ef-label">Title</label>
                            <input className="ef-input" type="text" value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} />
                            <label className="ef-label">Date</label>
                            <div className="date-row">
                              <input className="ef-input" type="date" value={toYMD(String(editDraft.date || ""))} onChange={(e) => setEditDraft({ ...editDraft, date: mergeDateTime(e.currentTarget.value, toHM(String(editDraft.date || ""))) })} />
                              <input className="ef-input" type="time" value={toHM(String(editDraft.date || ""))} onChange={(e) => setEditDraft({ ...editDraft, date: mergeDateTime(toYMD(String(editDraft.date || "")), e.currentTarget.value) })} />
                            </div>
                            <label className="ef-label">Subject</label>
                            <div className="subject-row">
                              <select className="ef-input" value={String(editDraft.subject || "")} onChange={(e) => setEditDraft({ ...editDraft, subject: e.currentTarget.value })}>
                                <option value="">(none)</option>
                                {allSubjects.map((subj) => (
                                  <option key={subj} value={subj}>
                                    {subj}
                                  </option>
                                ))}
                              </select>
                              <input className="ef-input" type="text" placeholder="Or type a subject" value={String(editDraft.subject || "")} onChange={(e) => setEditDraft({ ...editDraft, subject: e.currentTarget.value })} />
                            </div>
                            <label className="ef-label">Status & Visibility</label>
                            <div className="subject-row">
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                  type="checkbox"
                                  checked={String(editDraft.status || 'published').toLowerCase() === 'draft'}
                                  onChange={(e) => setEditDraft({ ...editDraft, status: e.currentTarget.checked ? 'draft' : 'published' })}
                                />
                                <span>Save as Draft</span>
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                  type="checkbox"
                                  checked={!!editDraft.hidden}
                                  onChange={(e) => setEditDraft({ ...editDraft, hidden: e.currentTarget.checked })}
                                />
                                <span>Hide from public listing</span>
                              </label>
                            </div>
                            <label className="ef-label">YouTube Video ID</label>
                            <input className="ef-input" type="text" placeholder="e.g. dQw4w9WgXcQ" value={editDraft.youtubeId || ""} onChange={(e) => setEditDraft({ ...editDraft, youtubeId: e.target.value })} />
                            <label className="ef-label">Image URL(s)</label>
                            <input className="ef-input" type="text" placeholder="Single URL or comma-separated" value={editDraft.imagesStr} onChange={(e) => setEditDraft({ ...editDraft, imagesStr: e.target.value })} />
                            {/* AI Image Generator (edit mode) */}
                            <div className="ai-wrap">
                              <div className="ai-row">
                                <input
                                  className="ef-input"
                                  type="password"
                                  placeholder="Stability API key (sk-...)"
                                  value={sdApiKey}
                                  onChange={(e) => {
                                    setSdApiKey(e.target.value);
                                    try {
                                      localStorage.setItem("stability_api_key", e.target.value);
                                    } catch {}
                                  }}
                                />
                                <button
                                  className="btn"
                                  onClick={() => {
                                    setSdApiKey("");
                                    try {
                                      localStorage.removeItem("stability_api_key");
                                    } catch {}
                                  }}
                                >
                                  Clear Key
                                </button>
                              </div>
                              <div className="ai-row">
                                <input className="ef-input" type="text" placeholder="Describe an image (e.g., Lighthouse on a cliff)" value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} />
                                <button className="btn btn-primary" onClick={generateImageForEdit} disabled={genLoading}>
                                  {genLoading ? "Generating..." : "Generate"}
                                </button>
                              </div>
                              {genError && <div className="ai-error">{genError}</div>}
                              {genPreviewUrl && (
                                <div className="ai-preview">
                                  <img src={genPreviewUrl} alt="Generated preview" />
                                </div>
                              )}
                            </div>
                            <label className="ef-label">Links (comma-separated)</label>
                            <input className="ef-input" type="text" value={editDraft.linksStr} onChange={(e) => setEditDraft({ ...editDraft, linksStr: e.target.value })} />
                            <label className="ef-label">Content (HTML)</label>
                            <textarea className="ef-textarea" rows={8} value={editDraft.content} onChange={(e) => setEditDraft({ ...editDraft, content: e.target.value })} />
                          </div>
                        ) : hasEmbeddable(post.Body) ? (
                          <div
                            className="post-html"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtmlAllowEmbeds(post.Body),
                            }}
                          />
                        ) : (
                          <p className="post-body">{removeHtmlTags(post.Body)}</p>
                        )}
                        {/* If there are multiple links, list them under the post */}
                        {(() => {
                          const links = normalizeLinks((post as any).links);
                          if (links.length > 1) {
                            return (
                              <div className="post-links" aria-label="Related links">
                                <div className="post-links-title">Links</div>
                                <ul className="post-links-list">
                                  {links.map((u, i) => (
                                    <li key={`${i}-${u}`}>
                                      <a href={u} target="_blank" rel="noopener noreferrer">
                                        {u}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {/* Comments only for KV-backed posts (editable posts) */}
                        {pid && post?._source === "kv" && <Comments postId={pid} initialComments={(post as any).comments || []} editMode={editMode} adminPw={adminPw} />}
                      </div>
                    </article>
                  </React.Fragment>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={currentPage === 0}>
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1}>
                  Next
                </button>
              </div>
            )}
          </>
        );
      })()}

      <style>
        {`
          .blog-brutalist {
            text-align: left;
            /* Controls for post list positioning/width */
            --post-list-narrow-by: 200px; /* reduce width by this amount */
            --post-list-shift-left: 0px; /* nudge list into the grid gap */
          }

          .blog-header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px 0 16px;
            /* Match post list width and left offset */
            width: calc(100% - var(--post-list-narrow-by));
            margin-left: calc(0px - var(--post-list-shift-left));
          }

          .count-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border: 3px solid #000;
            box-shadow: 4px 4px 0 #000;
            padding: 6px 10px;
            background: #fff;
            white-space: nowrap;
          }
          .count-badge--big {
            font-size: 1.1rem;
            padding: 10px 14px;
            align-self: stretch; /* match the height of wrapped subjects */
          }

          .post-list {
            display: grid;
            gap: 18px;
            /* Make the list 150px narrower and shift it left */
            width: calc(100% - var(--post-list-narrow-by));
            margin-left: calc(0px - var(--post-list-shift-left));
          }

          .post-card {
            border: 3px solid #000;
            background: #fff;
            box-shadow: 6px 6px 0 #000;
            padding: 16px 16px 14px;
            position: relative; /* enable watermark layering */
            overflow: hidden;   /* clip watermark edges inside card */
            display: grid;      /* two-column layout for dagger + content */
            grid-template-columns: 72px 1fr;
            column-gap: 12px;
            align-items: start;
          }

          /* Sun watermark: make it a bit darker */
          .post-card::before {
            content: "";
            position: absolute;
            right: -6%;
            bottom: -8%;
            width: clamp(180px, 45%, 520px);
            aspect-ratio: 1 / 1;
            background: url('/sun.png') center/contain no-repeat;
            opacity: 0.12; /* slightly stronger for darker appearance */
            filter: saturate(0.6) contrast(1.1) brightness(0.93);
            pointer-events: none; /* decorative only */
            z-index: 0;
          }

          /* Ensure content sits above the watermark */
          .post-card > * { position: relative; z-index: 1; }

          .post-col-left {
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding-top: 2px;
          }
          .post-col-main { min-width: 0; }

          .post-title {
            margin: 0 0 6px;
            font-weight: 900;
            line-height: 1.1;
            text-wrap: balance;
          }

          .post-anchor {
            display: inline-block;
            margin-right: 8px;
          }
          .post-col-left .post-anchor {
            display: block;
            margin: 0 auto;
          }
          .post-anchor-icon {
            width: 64px;   /* large but fits skinny column */
            height: auto;  /* preserve aspect ratio */
            display: inline-block;
            vertical-align: middle;
          }

          @media (max-width: 760px) {
            .post-card { grid-template-columns: 60px 1fr; }
            .post-anchor-icon { width: 52px; }
          }

          .post-meta {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            font-size: 0.9rem;
            border: 2px solid #000;
            display: inline-block;
            padding: 2px 6px;
            margin: 0 0 10px;
            background: #f8f8f8;
          }

          .post-body {
            margin: 0;
            line-height: 1.6;
          }

          .post-image {
            width: 100%;
            height: auto;
            display: block;
            margin: 0 0 10px;
          }

          .post-html {
            line-height: 1.6;
          }
          /* Links list rendering for posts with multiple links */
          .post-links {
            margin: 8px 0 0;
            padding: 8px 10px;
            background: #f8f8f8;
            border: 2px solid #000;
            box-shadow: 4px 4px 0 #000;
          }
          .post-links-title {
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            margin: 0 0 6px;
          }
          .post-links-list {
            margin: 0;
            padding-left: 18px;
          }
          .post-links-list li a {
            word-break: break-all;
          }
          /* Constrain arbitrary HTML content inside posts */
          .post-html img {
            max-width: 100%;
            height: auto;
            display: block;
          }
          .post-html iframe,
          .post-html object,
          .post-html embed,
          .post-html video,
          .post-html audio {
            max-width: 100%;
          }
          .post-html, .post-body {
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          /* Responsive YouTube embed */
          .yt-embed {
            width: 100%;
            aspect-ratio: 16 / 9;
            background: #000;
            margin: 0 0 10px;
            position: relative;
            overflow: hidden;
          }
          .yt-embed iframe {
            width: 100%;
            height: 100%;
            border: 0;
            position: absolute;
            inset: 0;
            display: block;
          }
          .post-html pre, .post-html code {
            white-space: pre-wrap;
            word-break: break-word;
          }

          /* Section headings to separate new vs older */
          .section-heading {
            margin: 18px 0 8px;
            padding: 8px 10px;
            border: 3px solid #000;
            box-shadow: 6px 6px 0 #000;
            display: inline-block;
            background: #fff;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .section-heading--latest {
            background: #d0ffd0; /* soft green */
          }
          .section-heading--older {
            background: #f0f0f0; /* light gray */
          }

          /* small hover nudge */
          .post-card:hover {
            transform: translate(-2px, -2px);
            box-shadow: 8px 8px 0 #000;
            transition: transform 80ms ease-out, box-shadow 80ms ease-out;
          }

          .pagination {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 10px 0 0;
          }

          .page-btn {
            border: 3px solid #000;
            box-shadow: 3px 3px 0 #000;
            background: #fff;
            padding: 6px 10px;
            font-weight: 700;
            cursor: pointer;
          }

          .page-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .page-info {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            font-size: 0.9rem;
          }

          /* Edit mode toolbar */
          .edit-toolbar {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 0 0 10px;
            border: 3px solid #000;
            box-shadow: 6px 6px 0 #000;
            background: #fff;
            padding: 8px 10px;
            /* Match post list width and left offset */
            width: calc(100% - var(--post-list-narrow-by));
            margin-left: calc(0px - var(--post-list-shift-left));
          }
          .edit-mode-pill {
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: .5px;
            border: 3px solid #000;
            padding: 4px 8px;
            background: #d0ffd0;
            box-shadow: 4px 4px 0 #000;
          }
          .pw-wrap { display: flex; align-items: center; gap: 8px; }
          .pw-wrap input {
            border: 3px solid #000;
            box-shadow: 4px 4px 0 #000;
            padding: 6px 8px;
            font-weight: 700;
          }

          .btn {
            border: 3px solid #000;
            box-shadow: 3px 3px 0 #000;
            background: #fff;
            padding: 6px 10px;
            font-weight: 800;
            cursor: pointer;
          }
          .btn-primary {
            background: #000;
            color: #fff;
          }
          .btn-danger {
            background: #b00020;
            color: #fff;
          }
          .btn:disabled { opacity: 0.6; cursor: not-allowed; }

          .edit-actions { display: flex; gap: 8px; margin: 6px 0; }
          .edit-form { display: grid; gap: 6px; margin: 8px 0; }
          .ef-label { font-weight: 700; }
          .ef-input, .ef-textarea {
            border: 3px solid #000;
            box-shadow: 4px 4px 0 #000;
            background: #fff;
            padding: 8px 10px;
            font-weight: 700;
            width: 100%;
          }
          .date-row, .subject-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }

          /* AI generator rows and feedback */
          .ai-row {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 8px;
            align-items: center;
            margin: 6px 0;
          }
          .ai-error { color: #b00020; font-size: 0.9rem; margin: 4px 0; }
          .ai-preview { border: 2px dashed #000; padding: 8px; border-radius: 6px; background: #f7f7f7; }
          .ai-preview img { width: 100%; height: auto; display: block; border-radius: 4px; }
          .post-title-row { display: flex; align-items: center; gap: 10px; }
          .gen-thumb { width: 48px; height: 48px; object-fit: cover; border: 2px solid #000; box-shadow: 3px 3px 0 #000; border-radius: 4px; }

          /* subject filter bar */
          .subject-filter {
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: 12px;
            align-items: stretch;
            margin: 0 0 14px;
            /* Match post list width and left offset */
            width: calc(100% - var(--post-list-narrow-by));
            margin-left: calc(0px - var(--post-list-shift-left));
          }

          .subject-buttons {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
          }

          .subject-select-wrap { display: none; }
          .subject-select {
            width: 100%;
            padding: 10px 12px;
            border: 3px solid #000;
            box-shadow: 4px 4px 0 #000;
            background: #fff;
            font-weight: 700;
          }

          /* no special rule needed; grid handles layout */

          .subject-btn {
            border: 3px solid #000;
            box-shadow: 3px 3px 0 #000;
            background: #fff;
            padding: 6px 10px;
            font-weight: 800;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }

          .subject-btn.active,
          .subject-btn:hover {
            background: #000;
            color: #fff;
          }

          /* Mobile simplifications */
          @media (max-width: 760px) {
            .subject-filter {
              grid-template-columns: 1fr;
              row-gap: 10px;
              /* Reset width/offset on mobile */
              width: 100%;
              margin-left: 0;
            }
            .subject-buttons { display: none; }
            .subject-select-wrap { display: block; }
            .count-badge--big { align-self: start; }
            /* Reset list width/position on small screens */
            .post-list {
              width: 100%;
              margin-left: 0;
            }
            .blog-header {
              width: 100%;
              margin-left: 0;
            }
          }
          /* Loading placeholder shown while fetching KV posts */
          .loading-card {
            border: 3px solid #000;
            box-shadow: 6px 6px 0 #000;
            background: #fff;
            padding: 18px;
            margin: 8px 0 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 84px;
          }
          .loading-inner { display: flex; gap: 12px; align-items: center; }
          .loading-text { font-weight: 900; text-transform: uppercase; letter-spacing: .6px; }
          .loading-dots { display: inline-flex; gap: 6px; align-items: center; }
          .loading-dots span {
            width: 10px; height: 10px; display: block; background: #000; border-radius: 50%; opacity: 0.2;
            transform: translateY(0);
            animation: loading-bounce 1s infinite linear;
          }
          .loading-dots span:nth-child(2) { animation-delay: 0.12s; }
          .loading-dots span:nth-child(3) { animation-delay: 0.24s; }
          @keyframes loading-bounce {
            0% { opacity: 0.2; transform: translateY(0); }
            50% { opacity: 1; transform: translateY(-8px); }
            100% { opacity: 0.2; transform: translateY(0); }
          }
        `}
      </style>
      {linksAdminOpen && <LinksAdmin onClose={() => setLinksAdminOpen(false)} adminPw={adminPw} setAdminPw={setAdminPw} />}
    </section>
  );
};

export default Blog;
